import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNotifications } from "../../../contexts/notification";
import { FaCheck } from "react-icons/fa";
import {
  VideoCallInterface,
  VideoCallNotification,
  AppointmentNotification,
  OnlineStatusIndicator,
  IncomingRequestNotification,
} from "./index";
import GreenPopupNotification from "../../common/NotificationSystem/GreenPopupNotification";
import RedPopupNotification from "../../common/NotificationSystem/RedPopupNotification";
import GrayPopupNotification from "../../common/NotificationSystem/GrayPopupNotification";

// Styled components for the demo container
const DemoContainer = styled.div`
  padding: 1.5rem;
  border-radius: 0 14px 14px 14px;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
  z-index: 1;

  /* Futuristic glassy border effect */
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);

  /* Space background gradients */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 15% 50%,
        rgba(99, 102, 241, 0.12) 0%,
        transparent 45%
      ),
      radial-gradient(
        circle at 85% 30%,
        rgba(124, 58, 237, 0.08) 0%,
        transparent 45%
      );
    z-index: -1;
  }

  /* Star-like dots in background */
  &:after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: -1;
    background-image: radial-gradient(
        1px 1px at 20% 30%,
        rgba(255, 255, 255, 0.8) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 40% 70%,
        rgba(255, 255, 255, 0.7) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 60% 40%,
        rgba(255, 255, 255, 0.8) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 80% 60%,
        rgba(255, 255, 255, 0.7) 1px,
        transparent 0
      );
    opacity: 0.4;
  }

  h4 {
    color: #e2e8f0;
    margin-top: 0;
  }
`;

const DemoTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #2196f3; /* Changed to a brighter blue color */

  span {
    color: #4caf50; /* Changed to a distinct green color */
    font-size: 0.8em;
  }
`;

const DemoControls = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.85rem;
  }
`;

const DemoButton = styled.button`
  padding: 0.75rem 1.25rem;
  background: rgba(79, 70, 229, 0.75);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.5);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  min-width: 120px; /* Fixed minimum width */
  height: 40px; /* Fixed height */
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;

  &:hover {
    background: rgba(79, 70, 229, 0.85);
    transform: translateY(-3px);
    box-shadow: 0 8px 25px -5px rgba(79, 70, 229, 0.5),
      0 0 15px rgba(99, 102, 241, 0.6);
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
  }

  &:disabled {
    background-color: rgba(148, 163, 184, 0.4);
    border: 1px solid rgba(148, 163, 184, 0.2);
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }

  /* Futuristic gradient overlay */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(129, 140, 248, 0.4) 0%,
      rgba(79, 70, 229, 0.4) 100%
    );
    z-index: -1;
  }
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.4);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.15);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15), 0 0 10px rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.3);
  }

  .avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    margin-right: 1rem;
    object-fit: cover;
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.3);
    border: 2px solid rgba(99, 102, 241, 0.2);
  }

  .user-info {
    flex: 1;

    h4 {
      margin: 0 0 0.25rem 0;
      color: #e2e8f0;
    }

    p {
      margin: 0;
      color: #94a3b8;
      font-size: 0.875rem;
    }
  }

  .status {
    margin-left: 1rem;
  }
`;

// Tab styling
const TabContainer = styled.div`
  display: flex;
  margin-bottom: 0;
`;

const Tab = styled.button`
  padding: 0.75rem 1.25rem;
  background: ${(props) =>
    props.active ? "rgba(79, 70, 229, 0.75)" : "rgba(15, 23, 42, 0.5)"};
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.5);
  border-bottom: ${(props) =>
    props.active ? "none" : "1px solid rgba(99, 102, 241, 0.5)"};
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  font-weight: 500;
  margin-right: 0.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  z-index: 2;
  &:hover {
    background: ${(props) =>
      props.active ? "rgba(79, 70, 229, 0.75)" : "rgba(79, 70, 229, 0.65)"};
  }

  /* Create a visual connection for the active tab with the container below */
  &::after {
    content: ${(props) => (props.active ? "''" : "none")};
    position: absolute;
    height: 2px;
    background: rgba(15, 23, 42, 0.65);
    bottom: -1px;
    left: 1px;
    right: 1px;
    z-index: 3;
  }
`;

// Add animated dots for loading
const AnimatedDots = styled.span`
  @keyframes dotsAnimation {
    0% {
      content: ".";
    }
    33% {
      content: "..";
    }
    66% {
      content: "...";
    }
    100% {
      content: ".";
    }
  }

  &::after {
    content: ".";
    animation: dotsAnimation 1.5s infinite;
  }
`;

const WaitingMessage = styled.div`
  background: rgba(15, 23, 42, 0.5);
  padding: 1rem;
  border-radius: 8px;
  color: #e2e8f0;
  text-align: center;
  margin: 1rem 0;
`;

// Count badge for incoming requests
const NotificationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: #e53e3e;
  color: white;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  margin-left: 8px;
`;

const VideoCallSystemDemo = () => {
  const { addNotification } = useNotifications();
  const [activeCall, setActiveCall] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [isExitingCall, setIsExitingCall] = useState(false);
  const [showAppointmentNotification, setShowAppointmentNotification] =
    useState(false);
  const [currentParticipant, setCurrentParticipant] = useState(null);
  const [activeTab, setActiveTab] = useState("request"); // 'request' or 'incoming'
  const [requestingUserId, setRequestingUserId] = useState(null); // ID of user being requested
  const [acceptedUserId, setAcceptedUserId] = useState(null); // ID of user who accepted request
  const [incomingRequest, setIncomingRequest] = useState(null); // Stores incoming appointment requests
  const [acceptedIncomingRequest, setAcceptedIncomingRequest] = useState(null); // Stores the accepted incoming request
  const [incomingRequestCount, setIncomingRequestCount] = useState(0); // Track number of incoming requests
  const [comingFromDashboard, setComingFromDashboard] = useState(true); // Track if component just mounted from dashboard
  // Popup notification states
  const [showRequestAcceptedPopup, setShowRequestAcceptedPopup] =
    useState(false);
  const [showIncomingAcceptedPopup, setShowIncomingAcceptedPopup] =
    useState(false);
  const [showRequestRejectedPopup, setShowRequestRejectedPopup] =
    useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  // Mock users data - Career counselors and SCAD office staff
  const users = [
    {
      id: 1,
      name: "Dr. James Miller",
      role: "Career Counselor",
      status: "online",
      avatar:
        "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=634&q=80",
    },
    {
      id: 2,
      name: "Jessica Thompson",
      role: "SCAD Office Staff",
      status: "online",
      avatar:
        "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=634&q=80",
    },
    {
      id: 3,
      name: "Dr. Robert Chen",
      role: "Faculty Advisor",
      status: "offline",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=634&q=80",
    },
  ];

  // Mock appointment data
  const mockAppointment = {
    id: 1,
    user: {
      name: "Dr. James Miller",
      role: "Career Counselor",
    },
    status: "accepted",
    date: "2025-05-16T15:30:00",
    topic: "Career Guidance Session",
  };
  // Automatically trigger appointment notification and incoming call when component mounts
  useEffect(() => {
    // Flag to ensure this only runs once per component mount
    const hasRun = sessionStorage.getItem(
      "proStudentAppointmentNotificationShown"
    );

    if (!hasRun) {
      // Mark notification as shown
      sessionStorage.setItem("proStudentAppointmentNotificationShown", "true");

      // Show appointment notification after 10 seconds
      const appointmentTimer = setTimeout(() => {
        setShowAppointmentNotification(true);
      }, 10000);

      // Simulate incoming call after 20 seconds
      const callTimer = setTimeout(() => {
        // Force an incoming call regardless of other states
        simulateIncomingCall(users[0]);
      }, 20000);

      return () => {
        clearTimeout(appointmentTimer);
        clearTimeout(callTimer);
      };
    }
  }, []);

  // Effect to trigger appointment request when coming from dashboard
  useEffect(() => {
    // Only run this effect once when the component is mounted
    if (comingFromDashboard) {
      setComingFromDashboard(false); // Reset the flag
      // Set a short timeout to simulate a bit of server delay
      const requestTimer = setTimeout(() => {
        // Add an incoming request and increment count
        if (users.length > 0) {
          setIncomingRequest(users[0]);
          setIncomingRequestCount(1);

          // Add notification about incoming request
          addNotification({
            title: "New Appointment Request",
            message: `${users[0].name} is requesting an appointment`,
            type: "info",
            forRole: "proStudent",
          });
        }
      }, 1000); // 1 second delay

      return () => {
        clearTimeout(requestTimer);
      };
    }
  }, [comingFromDashboard, users]);

  // Effect to trigger incoming appointment request when user clicks on the "incoming" tab
  useEffect(() => {
    if (
      activeTab === "incoming" &&
      !incomingRequest &&
      !acceptedIncomingRequest
    ) {
      // Set a short timeout to simulate a bit of server delay
      const requestTimer = setTimeout(() => {
        // Set an incoming request
        if (users.length > 0) {
          setIncomingRequest(users[0]);

          // Add notification about incoming request
          addNotification({
            title: "New Appointment Request",
            message: `${users[0].name} is requesting an appointment`,
            type: "info",
            forRole: "proStudent",
          });
        }
      }, 1000); // 1 second delay

      return () => {
        clearTimeout(requestTimer);
      };
    }
  }, [activeTab, incomingRequest, acceptedIncomingRequest, users]);
  // Simulate an incoming call - for compatibility with existing code
  const simulateIncomingCall = (user) => {
    setIncomingCall(user);

    // Auto-dismiss incoming call after 8 seconds if not accepted/rejected
    setTimeout(() => {
      setIncomingCall((currentCall) => {
        // Only clear if the incoming call is still the same (hasn't been handled)
        if (currentCall && currentCall.id === user.id) {
          // Add notification that call was missed
          addNotification({
            title: "Missed Call",
            message: `You missed a call from ${user.name}`,
            type: "info",
            forRole: "proStudent",
          });
          return null;
        }
        return currentCall;
      });
    }, 8000);
  };
  // Function to simulate random incoming calls for testing purposes
  const simulateRandomIncomingCall = () => {
    const onlineUsers = users.filter((user) => user.status === "online");
    if (onlineUsers.length > 0) {
      const randomUser =
        onlineUsers[Math.floor(Math.random() * onlineUsers.length)];
      simulateIncomingCall(randomUser);
    } else {
      // Show notification if no online users
      addNotification({
        title: "No Online Users",
        message: "Cannot simulate incoming call as no users are online",
        type: "info",
        forRole: "proStudent",
      });
    }
  };

  // Add a keyboard shortcut to trigger test calls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === "C") {
        simulateRandomIncomingCall();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [users]);

  // Clean up session storage when leaving the appointments tab
  useEffect(() => {
    return () => {
      // This will run when component unmounts
      sessionStorage.removeItem("proStudentAppointmentNotificationShown");
    };
  }, []); // Function to handle requesting an appointment
  const requestAppointment = (user) => {
    // Store the user ID being requested
    setRequestingUserId(user.id);

    // Simulate the server processing the request
    setTimeout(() => {
      setRequestingUserId(null);

      // Dr. Robert Chen will always reject requests (he's offline)
      // Jessica Thompson will also always reject requests
      if (user.id === 3 || user.id === 2) {
        // Show the red popup notification for rejection
        setPopupMessage(
          `Your appointment request with ${user.name} was rejected`
        );
        setShowRequestRejectedPopup(true);

        // Add rejection notification
        addNotification({
          title: "Appointment Request Rejected",
          message: `Your appointment request with ${user.name} was rejected`,
          type: "info",
          forRole: "proStudent",
        });
      } else {
        // Other users will accept as before
        setAcceptedUserId(user.id);

        // Show the green popup notification
        setPopupMessage(
          `Your appointment request with ${user.name} was accepted`
        );
        setShowRequestAcceptedPopup(true);

        // Also add to the notification system
        addNotification({
          title: "Appointment Request Accepted",
          message: `Your appointment request with ${user.name} was accepted`,
          type: "success",
          forRole: "proStudent",
        });
      }
    }, 3000);
  }; // Function to handle incoming appointment request
  const simulateIncomingRequest = () => {
    // Choose a random online user
    const onlineUsers = users.filter((user) => user.status === "online");
    if (onlineUsers.length > 0) {
      const randomUser =
        onlineUsers[Math.floor(Math.random() * onlineUsers.length)];
      setIncomingRequest(randomUser);
    }
  };
  // Function to accept an incoming appointment request
  const acceptAppointmentRequest = () => {
    // Store the accepted request
    setAcceptedIncomingRequest(incomingRequest);
    setCurrentParticipant(incomingRequest);

    // Show the green popup notification
    setPopupMessage(
      `You accepted an appointment request from ${incomingRequest.name}`
    );
    setShowIncomingAcceptedPopup(true);

    // Clear the incoming request and update counter
    setIncomingRequest(null);
    setIncomingRequestCount(0);

    // Also add to the notification system
    addNotification({
      title: "Appointment Request Accepted",
      message: `You accepted an appointment request from ${incomingRequest.name}`,
      type: "success",
      forRole: "proStudent",
    });
  };

  // Function to reject an incoming appointment request
  const rejectAppointmentRequest = () => {
    // Store the rejected request information for popup notification
    const rejectedUser = incomingRequest;

    // Clear the incoming request and update counter
    setIncomingRequest(null);
    setIncomingRequestCount(0);

    // Show red popup notification that request was rejected
    setPopupMessage(
      `You rejected the appointment request from ${rejectedUser.name}`
    );
    setShowIncomingAcceptedPopup(false); // Make sure no acceptance popup is showing
    setShowRequestRejectedPopup(true); // Show rejection popup

    // Use the notification system as well
    addNotification({
      title: "Appointment Request Rejected",
      message: `You rejected the appointment request from ${rejectedUser.name}`,
      type: "info",
      forRole: "proStudent",
    });
  };

  // Function to handle starting a call
  const startCall = (user) => {
    setCurrentParticipant(user);
    setActiveCall(true);
    setAcceptedUserId(null); // Clear accepted state when starting call
    setAcceptedIncomingRequest(null); // Clear accepted incoming request

    // Clear any popup notifications
    setShowRequestAcceptedPopup(false);
    setShowIncomingAcceptedPopup(false);
    setShowRequestRejectedPopup(false);

    addNotification({
      title: "Call Started",
      message: `You started a call with ${user.name}`,
      type: "info",
      forRole: "proStudent",
    });
  };
  // Function to handle ending a call
  const endCall = () => {
    setActiveCall(false);
    setCurrentParticipant(null);

    // Show gray notification when user manually ends call
    addNotification({
      component: GrayPopupNotification,
      props: {
        message: "You ended the call",
      },
    });
  };

  // Function to handle accepting an incoming call
  const acceptCall = (caller) => {
    setCurrentParticipant(caller);
    setActiveCall(true);
    setIncomingCall(null);
    setIsExitingCall(false);
  };

  // Function to handle rejecting an incoming call
  const rejectCall = () => {
    setIsExitingCall(true);
    setTimeout(() => {
      setIncomingCall(null);
      setIsExitingCall(false);
    }, 300); // Match the exit animation duration
  }; // Add a demo button to reset state (for testing only)
  const resetAppointmentState = () => {
    setAcceptedUserId(null);
    setRequestingUserId(null);
    setCurrentParticipant(null);
    setActiveCall(false);
    setIncomingCall(null);
    setIsExitingCall(false);
    setShowAppointmentNotification(false);
    setIncomingRequest(null);
    setAcceptedIncomingRequest(null);
    setShowRequestAcceptedPopup(false);
    setShowIncomingAcceptedPopup(false);
    setShowRequestRejectedPopup(false);
    setIncomingRequestCount(0);
    setComingFromDashboard(true);

    addNotification({
      title: "System Reset",
      message: "All appointment and call states have been reset",
      type: "info",
      forRole: "proStudent",
    });
  };
  // Add this useEffect to automatically end calls after 25 seconds
  useEffect(() => {
    // Set up timer to automatically end calls after 25 seconds
    let callEndTimer;
    if (activeCall && currentParticipant) {
      callEndTimer = setTimeout(() => {
        console.log("Automatically ending call after 25 seconds");
        // End the call
        setActiveCall(false);

        // Import and use the handleEndCallProStudent function
        const { handleEndCallProStudent } = require("./handleEndCall");
        const streamRef = { current: null }; // Dummy streamRef since we don't have a real stream
        const onEndCall = () => {
          console.log("Call ended callback triggered");
          setCurrentParticipant(null);
        };

        // Pass the automatic flag and ensure enough time to see the notification
        handleEndCallProStudent(
          streamRef,
          onEndCall,
          currentParticipant,
          addNotification,
          true
        );
      }, 25000);
    }

    // Cleanup timer when component unmounts or call ends
    return () => {
      if (callEndTimer) clearTimeout(callEndTimer);
    };
  }, [activeCall, currentParticipant, addNotification]);

  return (
    <div>
      <DemoTitle>Video Appointment System</DemoTitle>
      {/* Tab navigation */}{" "}
      <TabContainer>
        <Tab
          active={activeTab === "request"}
          onClick={() => setActiveTab("request")}
        >
          Request Appointment
        </Tab>
        <Tab
          active={activeTab === "incoming"}
          onClick={() => setActiveTab("incoming")}
        >
          Incoming Appointments
        </Tab>
      </TabContainer>
      {/* Request Tab */}
      {activeTab === "request" && (
        <DemoContainer>
          <h4>Available Career Advisors</h4>

          {users.map((user) => (
            <UserCard key={user.id}>
              <img src={user.avatar} alt={user.name} className="avatar" />
              <div className="user-info">
                <h4>{user.name}</h4>
                <p>{user.role}</p>
              </div>{" "}
              <div className="status">
                <OnlineStatusIndicator isOnline={user.status === "online"} />
              </div>{" "}
              {acceptedUserId === user.id ? (
                <DemoButton
                  onClick={() => startCall(user)}
                  disabled={activeCall}
                >
                  Start Call
                </DemoButton>
              ) : (
                <DemoButton
                  onClick={() => requestAppointment(user)}
                  disabled={
                    user.status === "offline" || requestingUserId !== null
                  }
                  style={{
                    background:
                      user.status === "offline" || requestingUserId !== null
                        ? "rgba(148, 163, 184, 0.4)"
                        : "rgba(79, 70, 229, 0.75)",
                    cursor:
                      user.status === "offline" || requestingUserId !== null
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      user.status === "offline" || requestingUserId !== null
                        ? 0.7
                        : 1,
                  }}
                >
                  {requestingUserId === user.id ? (
                    <AnimatedDots>Requesting</AnimatedDots>
                  ) : (
                    "Request"
                  )}
                </DemoButton>
              )}
            </UserCard>
          ))}
        </DemoContainer>
      )}
      {/* Incoming Requests Tab */}
      {activeTab === "incoming" && (
        <DemoContainer>
          <h4>Incoming Appointment Requests</h4>

          {incomingRequest ? (
            <UserCard key={incomingRequest.id}>
              <img
                src={incomingRequest.avatar}
                alt={incomingRequest.name}
                className="avatar"
              />
              <div className="user-info">
                <h4>{incomingRequest.name}</h4>
                <p>{incomingRequest.role}</p>
                <p>Requesting career guidance or report clarification</p>
              </div>
              <div className="status">
                <OnlineStatusIndicator isOnline={true} />
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <DemoButton onClick={acceptAppointmentRequest}>
                  Accept
                </DemoButton>
                <DemoButton
                  onClick={rejectAppointmentRequest}
                  style={{ background: "rgba(220, 53, 69, 0.75)" }}
                >
                  Reject
                </DemoButton>
              </div>
            </UserCard>
          ) : acceptedIncomingRequest ? (
            <UserCard key={acceptedIncomingRequest.id}>
              <img
                src={acceptedIncomingRequest.avatar}
                alt={acceptedIncomingRequest.name}
                className="avatar"
              />
              <div className="user-info">
                <h4>{acceptedIncomingRequest.name}</h4>
                <p>{acceptedIncomingRequest.role}</p>
              </div>
              <div className="status">
                <OnlineStatusIndicator isOnline={true} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <DemoButton
                  onClick={() => startCall(acceptedIncomingRequest)}
                  disabled={activeCall}
                >
                  Start Call
                </DemoButton>
              </div>
            </UserCard>
          ) : (
            <WaitingMessage>
              <p>No incoming appointment requests at the moment</p>
              <p
                style={{
                  fontSize: "0.8rem",
                  marginTop: "0.5rem",
                  color: "#94a3b8",
                }}
              >
                New requests will appear here automatically
              </p>
            </WaitingMessage>
          )}
        </DemoContainer>
      )}
      {/* Video Call Interface */}
      {activeCall && currentParticipant && (
        <VideoCallInterface
          participant={currentParticipant}
          onEndCall={endCall}
        />
      )}
      {/* Green popup notifications */}
      {showRequestAcceptedPopup && (
        <GreenPopupNotification
          title="Appointment Request Accepted"
          message={popupMessage}
          duration={4000}
          onClose={() => setShowRequestAcceptedPopup(false)}
        />
      )}
      {showIncomingAcceptedPopup && (
        <GreenPopupNotification
          title="Request Accepted"
          message={popupMessage}
          duration={4000}
          onClose={() => setShowIncomingAcceptedPopup(false)}
        />
      )}
      {showRequestRejectedPopup && (
        <RedPopupNotification
          title="Request Rejected"
          message={popupMessage}
          duration={4000}
          onClose={() => setShowRequestRejectedPopup(false)}
        />
      )}
      {/* Incoming Call Notification */}
      {incomingCall && (
        <VideoCallNotification
          caller={incomingCall}
          onAccept={acceptCall}
          onReject={rejectCall}
          isExiting={isExitingCall}
        />
      )}{" "}
      {/* Appointment Notification */}
      {showAppointmentNotification && (
        <AppointmentNotification
          appointment={mockAppointment}
          onClose={() => setShowAppointmentNotification(false)}
        />
      )}
    </div>
  );
};

export default VideoCallSystemDemo;
